import { Navigate } from 'react-router-dom'
import { useMe } from '../api'

function hasLoggedInCookie(): boolean {
  return document.cookie.includes('logged_in=1')
}

/**
 * Operator-only guard. Gates /app and anything else an access-key user sees.
 *
 * Auth flow:
 *   1. If logged_in cookie is absent → hard-redirect to /. No network round-trip.
 *      (Quick pre-check so F5 on a protected page doesn't flash the UI.)
 *   2. While useMe() is pending → spinner (not null — otherwise the route
 *      renders nothing and breaks test assertions / user perception).
 *   3. On 401 or role === 'admin' → redirect to / (admin goes to staff side).
 *   4. On role === 'operator' → render children.
 */
export function RequireUserAuth({ children }: { children: React.ReactNode }) {
  const { data, isPending, isError } = useMe()
  if (!hasLoggedInCookie()) return <Navigate to="/" replace />
  if (isPending) return <AuthSpinner />
  if (isError || !data) return <Navigate to="/" replace />
  if (data.role === 'admin') return <Navigate to="/staff" replace />
  if (data.role !== 'operator') return <Navigate to="/" replace />
  return <>{children}</>
}

function AuthSpinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950"
    >
      <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-700 border-t-blue-500 animate-spin" />
    </div>
  )
}
